import { useState, useRef, useEffect } from 'react';
import { useFilter } from '@/contexts/FiltersContext.context';
import './RadioGroup.scss';

export interface RadioOption {
  value: string;
  label: string;
  responsiveLabel?: string;
}

export interface RadioGroupProps {
  name: string;
  label?: string;
  options: RadioOption[];
  value?: string | null;
  defaultValue?: string;
  onChange?: (value: string) => void;
}

export function RadioGroup({ name, label, options = [], value, defaultValue, onChange }: RadioGroupProps) {
  const optionsRef = useRef<HTMLDivElement>(null);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });

  const [contextValue, setContextValue] = useFilter(name);

  const currentValue = value !== undefined
    ? value
    : (contextValue as string | null) ?? defaultValue ?? null;

  const updateSlider = () => {
    if (!optionsRef.current) return;
    const activeOption = optionsRef.current.querySelector('.radio-group__option--active') as HTMLElement;
    if (activeOption) {
      setSliderStyle({
        left: activeOption.offsetLeft,
        width: activeOption.offsetWidth,
      });
    }
  };

  useEffect(() => {
    updateSlider();
  }, [currentValue]);

  useEffect(() => {
    updateSlider();
    window.addEventListener('resize', updateSlider);
    return () => window.removeEventListener('resize', updateSlider);
  }, []);

  const handleChange = (newValue: string) => {
    setContextValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div className="radio-group">
      {label && <span className="radio-group__label font-bold" dangerouslySetInnerHTML={{ __html: label }} />}
      <div className="radio-group__options bg-background-primary-light" ref={optionsRef}>
        <span
          className={`radio-group__slider${currentValue ? ' radio-group__slider--active' : ''}`}
          style={{ left: sliderStyle.left, width: sliderStyle.width }}
          suppressHydrationWarning
        />
        {options.map((option) => (
          <label
            key={option.value}
            className={`radio-group__option${currentValue === option.value ? ' radio-group__option--active' : ''}`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={currentValue === option.value}
              onChange={() => handleChange(option.value)}
              className="radio-group__input"
            />
            <span className="radio-group__text">
              <span className="radio-group__text-desktop">{option.label}</span>
              <span className="radio-group__text-mobile">
                {option.responsiveLabel || option.label}
              </span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default RadioGroup;
